(() => {
  if (customElements.get('bundle-selector')) return;

  class BundleSelector extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.addButton = this.querySelector('[data-bundle-add]');
      this.errorWrapper = this.querySelector('[data-error-wrapper]');
      this.productPrice = this.getProductPriceRoot();

      this.addEventListener('change', this.onSelectionChange.bind(this));
      this.addButton?.addEventListener('click', this.onAddToCart.bind(this));
      this.syncSelection();
      this.updatePrice();
    }

    onSelectionChange(event) {
      if (!event.target.matches('[data-bundle-variant]')) return;

      const group = event.target.closest('[data-bundle-group]');
      this.markGroupSelection(group, event.target);
      this.updatePrice();
    }

    markGroupSelection(group, input) {
      if (!group) return;

      group.querySelectorAll('.bundle-selector__card, .swatch').forEach((el) => {
        el.classList.remove('is-selected', 'is-active');
      });

      const label = input.nextElementSibling;
      if (label?.classList.contains('bundle-selector__card')) {
        label.classList.add('is-selected');
      } else {
        label?.classList.add('is-active');
      }

      const name = group.querySelector('[data-bundle-selected-name]');
      if (name && input.dataset.optionName) {
        name.textContent = ` - ${input.dataset.optionName}`;
      }
    }

    syncSelection() {
      this.querySelectorAll('[data-bundle-group]').forEach((group) => {
        const selected = group.querySelector('[data-bundle-variant]:checked');
        if (selected) this.markGroupSelection(group, selected);
      });
    }

    selectedInputs() {
      return Array.from(this.querySelectorAll('[data-bundle-group]')).map((group) =>
        group.querySelector('[data-bundle-variant]:checked')
      );
    }

    updatePrice() {
      const inputs = this.selectedInputs();
      let total = 0;
      let compare = 0;
      let ready = true;

      inputs.forEach((input) => {
        if (!input) {
          ready = false;
          return;
        }
        total += Number(input.dataset.price) || 0;
        compare += Number(input.dataset.compareAtPrice) || Number(input.dataset.price) || 0;
      });

      if (this.addButton) this.addButton.disabled = !ready;
      if (ready) this.updateProductPrice(total, compare);
    }

    getProductPriceRoot() {
      const root = this.closest('[data-main-product]') || document;
      return root.querySelector('[id^="Product-Price-"] .price, .product__price .price');
    }

    updateProductPrice(total, compare) {
      const priceRoot = this.productPrice || this.getProductPriceRoot();
      if (!priceRoot) return;

      this.productPrice = priceRoot;
      const formatted = this.formatMoney(total);
      const compareFormatted = this.formatMoney(compare);
      const onSale = compare > total;
      const regular = priceRoot.querySelector('.price__regular span:not(.visually-hidden)');
      const saleIns = priceRoot.querySelector('.price__sale-inner ins');
      const saleDel = priceRoot.querySelector('.price__sale-inner del');

      priceRoot.classList.toggle('price--on-sale', onSale);

      if (regular) regular.textContent = formatted;
      if (saleIns) saleIns.textContent = formatted;
      if (saleDel) saleDel.textContent = compareFormatted;
    }

    formatMoney(cents) {
      const format = this.dataset.moneyFormat || '${{amount}}';
      const placeholder = /\{\{\s*(\w+)\s*\}\}/;
      const match = format.match(placeholder);
      if (!match) return String(cents);

      const amount = Number(cents) || 0;
      const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
        const value = (Math.abs(number) / 100).toFixed(precision);
        const parts = value.split('.');
        parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
        return parts.join(decimal);
      };

      let formatted = formatWithDelimiters(amount);
      switch (match[1]) {
        case 'amount_no_decimals':
          formatted = formatWithDelimiters(amount, 0);
          break;
        case 'amount_with_comma_separator':
          formatted = formatWithDelimiters(amount, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          formatted = formatWithDelimiters(amount, 0, '.', ',');
          break;
        default:
          break;
      }

      return format.replace(placeholder, formatted);
    }

    getCartAddPayload(cartDrawer) {
      const productTitle = this.dataset.productTitle || '';
      const attributeKey = this.dataset.bundleAttribute || 'Bundled Product For';
      const items = this.selectedInputs()
        .filter(Boolean)
        .map((input) => ({
          id: Number(input.value),
          quantity: 1,
          properties: {
            [attributeKey]: productTitle
          }
        }));

      const payload = { items };
      if (cartDrawer?.getSectionsToRender) {
        payload.sections = cartDrawer.getSectionsToRender().map((section) => section.section);
        payload.sections_url = window.location.pathname;
      }
      return payload;
    }

    handleError(message) {
      if (!this.errorWrapper) return;
      this.errorWrapper.classList.toggle('hidden', !message);
      this.errorWrapper.textContent = message || '';
    }

    async onAddToCart(event) {
      event.preventDefault();
      if (!this.addButton || this.addButton.disabled) return;

      this.handleError('');
      this.addButton.classList.add('disabled');

      const cartDrawer = document.querySelector('cart-drawer');
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.headers['Content-Type'] = 'application/json';
      config.body = JSON.stringify(this.getCartAddPayload(cartDrawer));

      try {
        const response = await fetch(`${routes.cart_add_url}`, config);
        const data = await response.json();

        if (data.status) {
          this.handleError(data.description);
          return;
        }

        const cartResponse = await this.withCartSections(data, cartDrawer);
        if (cartDrawer?.renderContents) {
          cartDrawer.renderContents(cartResponse);
        }
      } catch (error) {
        this.handleError(error.message);
      } finally {
        this.addButton.classList.remove('disabled');
      }
    }

    async withCartSections(response, cartDrawer) {
      if (response?.sections || !cartDrawer?.getSectionsToRender) return response;

      const sectionIds = cartDrawer
        .getSectionsToRender()
        .map((section) => section.section)
        .join(',');
      const root = window.Shopify?.routes?.root || '/';
      const sections = await fetch(`${root}?sections=${sectionIds}`).then((res) => res.json());
      return { ...response, sections };
    }
  }

  customElements.define('bundle-selector', BundleSelector);
})();
